import React, { Fragment } from "react";

import styled, { css } from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import { getButtonClassName } from "../utilities";

const TabNavigation = styled.div`
  display: flex;
  flex-direction: row;
`;

const TabNavigationChild = styled.div<{
  $selected?: boolean;
  $href?: string;
  $hideNonSelectedUnderline?: boolean;
  $disabled?: boolean;
}>`
  cursor: ${({ $selected, $disabled }) =>
    $disabled ? "not-allowed" : $selected ? "default" : "pointer"};
  color: ${({ $selected, $disabled }) =>
    $disabled
      ? colorTheme("neutralL3")
      : $selected
        ? colorTheme("primary")
        : colorTheme("neutralL1")};
  display: flex;
  align-items: center;
  padding: ${gridSpacing[4]}px;
  ${({ $href, $selected, $hideNonSelectedUnderline, $disabled }) =>
    !$href &&
    css`
      border-bottom: ${$selected && !$disabled
        ? `2px solid ${colorTheme("primary")}`
        : $hideNonSelectedUnderline
          ? "none"
          : `1px solid ${colorTheme("neutralL4")}`};
    `}
  font-size: 14px;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  ${({ $selected, $disabled }) =>
    !$selected &&
    !$disabled &&
    css`
      &:hover {
        color: ${colorTheme("neutral")};
      }
    `}
`;

const RemainderLine = styled.div<{
  $hideNonSelectedUnderline?: boolean;
}>`
  flex-grow: 1;
  ${({ $hideNonSelectedUnderline }) =>
    !$hideNonSelectedUnderline &&
    css`
      border-bottom: 1px solid ${colorTheme("neutralL4")};
    `}
`;

export interface TabObject<TData extends string = string> {
  key: TData;
  title: string;
  icon?: string;
  hasError?: boolean;
  href?: string;
  newTab?: boolean;
  count?: number;
  disabled?: boolean;
}

interface TabHeaderProps<TData extends string> {
  currentTab?: TData;
  onSelect?: (tabKey: TData) => void;
  style?: React.CSSProperties;
  tabStyle?: React.CSSProperties;
  hideNonSelectedUnderline?: boolean;
  testId?: string;
  tabs: TabObject<TData>[];
}

function TabHeader<TData extends string>({
  currentTab,
  onSelect,
  style,
  tabStyle,
  hideNonSelectedUnderline,
  testId,
  tabs,
}: TabHeaderProps<TData>) {
  return (
    <div style={style}>
      <TabNavigation>
        {tabs.map((tab, i) => {
          const isDisabled = !!tab.disabled;

          const tabElement = (
            <TabNavigationChild
              $hideNonSelectedUnderline={hideNonSelectedUnderline}
              onClick={() => {
                if (tab.href || isDisabled) return;
                onSelect && onSelect(tab.key as TData);
              }}
              $selected={currentTab === tab.key}
              $disabled={isDisabled}
              style={tabStyle}
              $href={tab.href}
              data-testid={`${testId}-tab-${tab.key}`}
            >
              <div
                style={{
                  color: isDisabled
                    ? colorTheme("neutralL3")
                    : tab.href
                      ? colorTheme("primary")
                      : "inherit",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {tab.hasError && !isDisabled && (
                  <i
                    className="fa-solid fa-circle-exclamation"
                    style={{
                      paddingRight: gridSpacing[2],
                      color: colorTheme("danger"),
                    }}
                  />
                )}
                {tab.icon && (
                  <i
                    className={getButtonClassName(tab.icon)}
                    style={{
                      paddingRight: gridSpacing[2],
                      opacity: isDisabled ? 0.6 : 1,
                    }}
                  />
                )}
                {tab.title}
                {tab.count !== undefined && (
                  <div
                    style={{
                      padding: `2px ${gridSpacing[2]}px`,
                      backgroundColor: colorTheme("neutralL4"),
                      color: isDisabled
                        ? colorTheme("neutralL3")
                        : colorTheme("neutralL1"),
                      marginLeft: gridSpacing[2],
                      borderRadius: gridSpacing[3],
                      fontSize: 12,
                      opacity: isDisabled ? 0.6 : 1,
                    }}
                  >
                    {tab.count}
                  </div>
                )}
                {tab.href && !isDisabled && (
                  <i
                    className="fa-solid fa-arrow-up-right-from-square"
                    style={{
                      marginLeft: gridSpacing[2],
                      color: colorTheme("primary"),
                    }}
                  />
                )}
              </div>
            </TabNavigationChild>
          );

          return (
            <Fragment key={i}>
              {tab.href && !isDisabled ? (
                <a
                  style={{
                    marginBottom: gridSpacing[6],
                    ...(!hideNonSelectedUnderline && {
                      borderBottom: `1px solid ${colorTheme("neutralL4")}`,
                      borderBottomWidth: 1,
                    }),
                    display: "block",
                  }}
                  href={tab.href}
                  target={
                    !!tab.newTab || tab.newTab === undefined
                      ? "_blank"
                      : undefined
                  }
                  rel="noreferrer"
                >
                  {tabElement}
                </a>
              ) : (
                tabElement
              )}
              {i !== tabs.length - 1 && (
                <RemainderLine
                  $hideNonSelectedUnderline={hideNonSelectedUnderline}
                  style={{ ...tabStyle, maxWidth: gridSpacing[6] }}
                />
              )}
            </Fragment>
          );
        })}
        <RemainderLine
          $hideNonSelectedUnderline={hideNonSelectedUnderline}
          style={{ ...tabStyle }}
        />
      </TabNavigation>
    </div>
  );
}

export default TabHeader;
